'use client'

import React, { useCallback, useRef, type DragEvent } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type ReactFlowInstance,
  type Connection,
  type IsValidConnection,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useAuth } from '@/hooks/auth/use-auth'
import { nodeTypes } from './nodes'
import { edgeTypes } from './edges'
import { FlowSidebar } from './FlowSidebar'
import type { FlowNode, FlowEdge, NodeType, NodeData } from '@/lib/pbx/flow/types'

interface FlowCanvasProps {
  nodes: FlowNode[]
  edges: FlowEdge[]
  onNodesChange: OnNodesChange
  onEdgesChange: OnEdgesChange
  onConnect: OnConnect
  onNodeSelect: (node: FlowNode | null) => void
  onNodesAdd: (nodes: FlowNode[]) => void
}

// Generate unique ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Default data for each node type
const getDefaultNodeData = (type: NodeType, userTimezone: string = 'UTC'): NodeData => {
  switch (type) {
    case 'incoming_call':
      return {}
    case 'ai_agent':
      return {
        agent_id: '',
        external_agent_id: '',
        human_handover_enabled: false,
        human_handover_phone: '',
      }
    case 'operator':
      return {
        phone_numbers: [],
        timeout: 30,
        simulring: true,
        caller_id_mode: 'customer',
      }
    case 'voicemail':
      return {
        max_duration: 120,
        transcribe: false,
      }
    case 'play_message':
      return {
        message_type: 'tts',
        tts_language: 'en-US',
      }
    case 'time_check':
      return {
        timezone: userTimezone,
        schedule: {
          default: {
            days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
            time_ranges: [{ start: '09:00', end: '18:00' }],
          },
          overrides: {},
        },
      }
    case 'hangup':
      return {}
    default:
      return {}
  }
}

export function FlowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeSelect,
  onNodesAdd,
}: FlowCanvasProps) {
  const { user } = useAuth()
  const userTimezone = user?.timezone
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null)

  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance
  }, [])

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const addNodeAtPosition = useCallback((type: NodeType, position: { x: number; y: number }) => {
    onNodesAdd([{
      id: generateId(),
      type,
      position,
      data: getDefaultNodeData(type, userTimezone),
    }])
  }, [onNodesAdd, userTimezone])

  const handleAddNode = useCallback((type: NodeType) => {
    const bounds = reactFlowWrapper.current?.getBoundingClientRect()
    const instance = reactFlowInstance.current
    if (!bounds || !instance) return

    // Keep tap insertion in the visible canvas even after panning or zooming.
    const position = instance.screenToFlowPosition({
      x: bounds.left + bounds.width / 2,
      y: bounds.top + bounds.height / 2,
    })
    addNodeAtPosition(type, { x: position.x - 100, y: position.y - 40 })
  }, [addNodeAtPosition])

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()

      const type = event.dataTransfer.getData('application/reactflow') as NodeType

      if (!type || !reactFlowInstance.current) {
        return
      }

      // screenToFlowPosition already handles the viewport offset,
      // so we pass screen coordinates directly
      const position = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      addNodeAtPosition(type, position)
    },
    [addNodeAtPosition]
  )

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeSelect(node as unknown as FlowNode)
    },
    [onNodeSelect]
  )

  const handlePaneClick = useCallback(() => {
    onNodeSelect(null)
  }, [onNodeSelect])

  // Custom connection validator
  const isValidConnection: IsValidConnection = useCallback((connection: Connection | Edge) => {
    // Prevent self-connections
    if (connection.source === connection.target) {
      return false
    }
    return true
  }, [])

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-row">
      <FlowSidebar onAddNode={handleAddNode} />
    <div ref={reactFlowWrapper} className="min-h-0 min-w-0 flex-1">
      <ReactFlow
        nodes={nodes as Node[]}
        edges={edges as Edge[]}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        isValidConnection={isValidConnection}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        deleteKeyCode={['Backspace', 'Delete']}
        defaultEdgeOptions={{
          type: 'deletable',
          animated: true,
          deletable: true,
        }}
      >
        <Background gap={15} />
        <Controls />
        <MiniMap
          className="!hidden md:!block"
          nodeStrokeWidth={3}
          zoomable
          pannable
        />
      </ReactFlow>
    </div>
    </div>
  )
}
